/**
 * Admin AI Models - Zod Schemas
 *
 * API contract schemas for the admin AI models management endpoints.
 * These models represent the global catalog of AI models available on the platform.
 */

import { z } from "zod";

import { DbEnum } from "../../../generated/prisma-enums";
import { LongContextPricingSchema } from "../../../models/model-catalog.config";
import { ThinkingDialectSchema } from "../../../models/thinking-dialect.registry";

// ============================================
// Base Schemas
// ============================================

const ModelProviderEnum = z.enum(DbEnum.MODEL_PROVIDER_VALUES);
const AiModelResolutionEnum = z.enum(["PENDING", "APPROVED", "IGNORED"]);
export type AiModelResolutionValue = z.infer<typeof AiModelResolutionEnum>;
// These are stored as plain strings in the DB, not Prisma enums
const SystemRoleEnum = z.string();
const ApiTypeEnum = z.string();

/**
 * Full AI Model schema — represents a record in the ai_model table.
 */
export const AiModelSchema = z.object({
  id: z.string().uuid(),
  modelId: z.string().min(1).max(100),
  provider: ModelProviderEnum,
  displayName: z.string().min(1).max(255),
  modelDisplayName: z.string().max(100),
  modelName: z.string().min(1).max(100),
  enabled: z.boolean(),
  resolution: AiModelResolutionEnum,
  isCustom: z.boolean(),
  deprecated: z.boolean(),

  // Pricing (USD/1M tokens)
  inputTokenPrice: z.number().min(0),
  outputTokenPrice: z.number().min(0),
  cacheReadTokenPrice: z.number().min(0).nullable(),
  cacheWriteTokenPrice: z.number().min(0).nullable(),
  longContextPricing: LongContextPricingSchema.nullable(),

  // Capabilities
  contextSize: z.number().int().positive().nullable(),
  streaming: z.boolean(),

  // Agent runtime
  systemRole: SystemRoleEnum.nullable(),
  codeInterpreter: z.boolean(),
  underlyingModel: z.string().nullable(),
  claudeCodeModelId: z.string().nullable(),
  fastMode: z.boolean(),

  // Thinking / reasoning dialect (replaces the four legacy capability booleans)
  thinkingDialect: ThinkingDialectSchema.nullable(),
  maxOutputTokens: z
    .number()
    .int()
    .positive()
    .describe(
      "Model capability ceiling — the max output tokens the provider API accepts for this model. " +
        "Sent as the default max_tokens / maxOutputTokens when no consumer budget is set; " +
        "upper clamp for any consumer budget."
    ),

  apiType: ApiTypeEnum.nullable(),

  // Admin
  notes: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});

export type AiModel = z.infer<typeof AiModelSchema>;

// ============================================
// API Contract Schemas
// ============================================

export const ZAiModelList = {
  method: "GET" as const,
  path: "/api/admin/ai-models" as const,
  Query: z.object({
    provider: ModelProviderEnum.optional(),
    enabled: z
      .string()
      .optional()
      .transform((v) => (v === "true" ? true : v === "false" ? false : undefined)),
    isCustom: z
      .string()
      .optional()
      .transform((v) => (v === "true" ? true : v === "false" ? false : undefined)),
    resolution: AiModelResolutionEnum.optional(),
    search: z.string().optional(),
    deprecated: z
      .string()
      .optional()
      .transform((v) => (v === "true" ? true : v === "false" ? false : undefined))
  }),
  Response: z.object({
    models: z.array(AiModelSchema),
    total: z.number()
  })
} as const;

export type ZAiModelListQuery = z.infer<typeof ZAiModelList.Query>;
export type ZAiModelListResponse = z.infer<typeof ZAiModelList.Response>;

export const ZAiModelPendingCount = {
  method: "GET" as const,
  path: "/api/admin/ai-models/pending-count" as const,
  Response: z.object({ count: z.number() })
} as const;

export type ZAiModelPendingCountResponse = z.infer<typeof ZAiModelPendingCount.Response>;

export const ZAiModelGet = {
  method: "GET" as const,
  path: "/api/admin/ai-models/:modelId" as const,
  PathVars: z.object({ modelId: z.string() }),
  Response: AiModelSchema
} as const;

export type ZAiModelGetPathVars = z.infer<typeof ZAiModelGet.PathVars>;
export type ZAiModelGetResponse = z.infer<typeof ZAiModelGet.Response>;

export const ZAiModelCreate = {
  method: "POST" as const,
  path: "/api/admin/ai-models" as const,
  Body: z.object({
    modelId: z.string().min(1).max(100),
    provider: ModelProviderEnum,
    displayName: z.string().trim().min(1).max(255),
    modelDisplayName: z.string().max(100).optional(),
    modelName: z.string().min(1).max(100),
    enabled: z.boolean().optional(),
    // Pricing
    inputTokenPrice: z.number().min(0),
    outputTokenPrice: z.number().min(0),
    cacheReadTokenPrice: z.number().min(0).optional(),
    cacheWriteTokenPrice: z.number().min(0).optional(),
    longContextPricing: LongContextPricingSchema.nullable().optional(),
    // Capabilities
    contextSize: z.number().int().positive().optional(),
    streaming: z.boolean().optional(),
    // Agent runtime
    systemRole: SystemRoleEnum.optional(),
    codeInterpreter: z.boolean().optional(),
    underlyingModel: z.string().optional(),
    fastMode: z.boolean().optional(),
    // Thinking / reasoning dialect (replaces the four legacy capability booleans)
    thinkingDialect: ThinkingDialectSchema.optional(),
    maxOutputTokens: z.number().int().positive(),
    apiType: ApiTypeEnum.optional(),
    // Admin
    notes: z.string().optional()
  }),
  Response: AiModelSchema
} as const;

export type ZAiModelCreateBody = z.infer<typeof ZAiModelCreate.Body>;
export type ZAiModelCreateResponse = z.infer<typeof ZAiModelCreate.Response>;

export const ZAiModelUpdate = {
  method: "PATCH" as const,
  path: "/api/admin/ai-models/:modelId" as const,
  PathVars: z.object({ modelId: z.string() }),
  // Only admin-configurable fields. Factory/routing fields (streaming, contextSize,
  // systemRole, apiType, codeInterpreter, underlyingModel, fastMode, thinkingDialect)
  // are catalog-owned: a changed value reaches stored rows through the data migration
  // packages/types/src/models/README.md § Stored rows describes, and they are not
  // editable through the admin UI.
  Body: z.object({
    // Branding
    displayName: z.string().trim().min(1).max(255).optional(),
    modelDisplayName: z.string().max(100).optional(),
    // Pricing (USD/1M tokens — changes frequently, legitimately admin-owned)
    inputTokenPrice: z.number().min(0).optional(),
    outputTokenPrice: z.number().min(0).optional(),
    cacheReadTokenPrice: z.number().min(0).nullable().optional(),
    cacheWriteTokenPrice: z.number().min(0).nullable().optional(),
    longContextPricing: LongContextPricingSchema.nullable().optional(),
    // Lifecycle
    enabled: z.boolean().optional(),
    deprecated: z.boolean().optional(),
    // Admin metadata
    notes: z.string().nullable().optional()
  }),
  Response: AiModelSchema
} as const;

export type ZAiModelUpdatePathVars = z.infer<typeof ZAiModelUpdate.PathVars>;
export type ZAiModelUpdateBody = z.infer<typeof ZAiModelUpdate.Body>;
export type ZAiModelUpdateResponse = z.infer<typeof ZAiModelUpdate.Response>;

export const ZAiModelDelete = {
  method: "DELETE" as const,
  path: "/api/admin/ai-models/:modelId" as const,
  PathVars: z.object({ modelId: z.string() }),
  Response: z.object({ success: z.literal(true) })
} as const;

export type ZAiModelDeletePathVars = z.infer<typeof ZAiModelDelete.PathVars>;
export type ZAiModelDeleteResponse = z.infer<typeof ZAiModelDelete.Response>;

export const ZAiModelToggle = {
  method: "POST" as const,
  path: "/api/admin/ai-models/:modelId/toggle" as const,
  PathVars: z.object({ modelId: z.string() }),
  Response: AiModelSchema
} as const;

export type ZAiModelTogglePathVars = z.infer<typeof ZAiModelToggle.PathVars>;
export type ZAiModelToggleResponse = z.infer<typeof ZAiModelToggle.Response>;

export const ZAiModelResolve = {
  method: "POST" as const,
  path: "/api/admin/ai-models/:modelId/resolve" as const,
  PathVars: z.object({ modelId: z.string() }),
  Body: z.object({
    action: z.enum(["APPROVE", "IGNORE"]),
    enabled: z.boolean().optional()
  }),
  Response: AiModelSchema
} as const;

export type ZAiModelResolvePathVars = z.infer<typeof ZAiModelResolve.PathVars>;
export type ZAiModelResolveBody = z.infer<typeof ZAiModelResolve.Body>;
export type ZAiModelResolveResponse = z.infer<typeof ZAiModelResolve.Response>;

export const ZAiModelSync = {
  method: "POST" as const,
  path: "/api/admin/ai-models/sync" as const,
  Response: z.object({ added: z.number() })
} as const;

export type ZAiModelSyncResponse = z.infer<typeof ZAiModelSync.Response>;

/**
 * Reset a single model to factory defaults
 * POST /api/admin/ai-models/:modelId/reset
 * Preserves: enabled, resolution, notes
 * Resets: all config/runtime/pricing fields
 */
export const ZAiModelReset = {
  method: "POST" as const,
  path: "/api/admin/ai-models/:modelId/reset" as const,
  PathVars: z.object({ modelId: z.string() }),
  Response: AiModelSchema
} as const;

export type ZAiModelResetPathVars = z.infer<typeof ZAiModelReset.PathVars>;
export type ZAiModelResetResponse = z.infer<typeof ZAiModelReset.Response>;

/**
 * Reset all non-custom models to factory defaults
 * POST /api/admin/ai-models/reset-all
 * Removes stale models, resets existing, inserts new — custom models untouched
 */
export const ZAiModelResetAll = {
  method: "POST" as const,
  path: "/api/admin/ai-models/reset-all" as const,
  Response: z.object({
    deleted: z.number(),
    reset: z.number()
  })
} as const;

export type ZAiModelResetAllResponse = z.infer<typeof ZAiModelResetAll.Response>;

/**
 * GET /api/admin/ai-models/model-health
 * Returns all model names in use (Agent + AITask + AgentTask) that are not in the active catalog.
 */

export const DeadModelResourcesSchema = z.object({
  modelName: z.string(),
  /**
   * Provider from the ai_model catalog row.
   * Null when the model was hard-deleted from the DB entirely.
   * Non-null when the model still exists in the DB but is deprecated.
   */
  provider: ModelProviderEnum.nullable(),
  /**
   * True when the model still exists in the DB but is marked deprecated.
   * False when the model was hard-deleted (and provider is null).
   * Use this to show different migration messaging in the UI.
   */
  deprecated: z.boolean(),
  agentCount: z.number(),
  taskCount: z.number(),
  workflowNodeCount: z.number()
});

export type DeadModelResources = z.infer<typeof DeadModelResourcesSchema>;

export const ZAiModelHealth = {
  method: "GET" as const,
  path: "/api/admin/ai-models/model-health" as const,
  Response: z.object({
    deadModels: z.array(DeadModelResourcesSchema),
    totalAffected: z.number()
  })
} as const;

export type ZAiModelHealthResponse = z.infer<typeof ZAiModelHealth.Response>;
